import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  IconButton,
  alpha,
  useTheme,
  Paper,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
} from 'recharts';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import PieChartIcon from '@mui/icons-material/PieChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { gmailExpensesService, ExpenseDashboard as ExpenseDashboardData } from '../services/gmailExpenses';

// Professional color palette - Using solid colors for MUI functions
const CHART_COLORS = {
  primary: '#6366F1',
  primaryLight: '#818CF8',
  secondary: '#10B981',
  secondaryLight: '#34D399',
  accent: '#F59E0B',
  accentLight: '#FBBF24',
  warning: '#EF4444',
  warningLight: '#F87171',
  info: '#3B82F6',
  infoLight: '#60A5FA',
  purple: '#8B5CF6',
  purpleLight: '#A78BFA',
  pink: '#EC4899',
  pinkLight: '#F472B6',
  teal: '#14B8A6',
  tealLight: '#2DD4BF',
  indigo: '#4F46E5',
  indigoLight: '#6366F1',
  rose: '#F43F5E',
  roseLight: '#FB7185',
  amber: '#FBBF24',
  amberLight: '#FCD34D',
  emerald: '#34D399',
  emeraldLight: '#6EE7B7',
};

// Solid colors array for donut chart (for alpha function)
const DONUT_COLORS_SOLID = [
  CHART_COLORS.primary,
  CHART_COLORS.secondary,
  CHART_COLORS.accent,
  CHART_COLORS.warning,
  CHART_COLORS.purple,
  CHART_COLORS.pink,
  CHART_COLORS.info,
  CHART_COLORS.teal,
];

// Gradient definitions (only for background styles, not for MUI functions)
const GRADIENTS = {
  primary: `linear-gradient(90deg, ${CHART_COLORS.primary} 0%, ${CHART_COLORS.indigo} 100%)`,
  secondary: `linear-gradient(90deg, ${CHART_COLORS.secondary} 0%, ${CHART_COLORS.emerald} 100%)`,
  area: `linear-gradient(180deg, rgba(99, 102, 241, 0.2) 0%, rgba(99, 102, 241, 0.05) 100%)`,
  donut: [
    `linear-gradient(135deg, ${CHART_COLORS.primary} 0%, ${CHART_COLORS.indigo} 100%)`,
    `linear-gradient(135deg, ${CHART_COLORS.secondary} 0%, ${CHART_COLORS.emerald} 100%)`,
    `linear-gradient(135deg, ${CHART_COLORS.accent} 0%, ${CHART_COLORS.amber} 100%)`,
    `linear-gradient(135deg, ${CHART_COLORS.warning} 0%, ${CHART_COLORS.rose} 100%)`,
    `linear-gradient(135deg, ${CHART_COLORS.purple} 0%, ${CHART_COLORS.pink} 100%)`,
    `linear-gradient(135deg, ${CHART_COLORS.pink} 0%, ${CHART_COLORS.rose} 100%)`,
    `linear-gradient(135deg, ${CHART_COLORS.info} 0%, ${CHART_COLORS.primary} 100%)`,
    `linear-gradient(135deg, ${CHART_COLORS.teal} 0%, ${CHART_COLORS.secondary} 100%)`,
  ],
};

const ExpenseDashboard: React.FC = () => {
  const [data, setData] = useState<ExpenseDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const theme = useTheme();

  useEffect(() => {
    loadDashboard();
  }, [days]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const dashboardData = await gmailExpensesService.getDashboard(days);
      setData(dashboardData);
    } catch (err: any) {
      console.error('Failed to load dashboard:', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // Custom Tooltip Components
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          elevation={8}
          sx={{
            p: 2.5,
            bgcolor: 'background.paper',
            border: 'none',
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ color: CHART_COLORS.primary }}>
            {label}
          </Typography>
          {payload.map((pld: any, index: number) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                mb: 1,
                minWidth: 160,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: pld.color || CHART_COLORS.primary,
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {pld.name}:
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={700} sx={{ color: CHART_COLORS.primary }}>
                {data?.currency || '$'} {pld.value.toFixed(2)}
              </Typography>
            </Box>
          ))}
        </Paper>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0];
      return (
        <Paper
          elevation={8}
          sx={{
            p: 2.5,
            bgcolor: 'background.paper',
            border: 'none',
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '4px',
                background: item.payload.fill,
              }}
            />
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: CHART_COLORS.primary }}>
              {item.payload.name}
            </Typography>
          </Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: CHART_COLORS.primary }}>
            {data?.currency || '$'} {item.value.toFixed(2)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {item.payload.percentage?.toFixed(1) || '0'}% of total
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
        sx={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ mb: 3, color: CHART_COLORS.primary }} />
        <Typography variant="h6" color="text.secondary" fontWeight={500}>
          Loading your expense insights...
        </Typography>
      </Box>
    );
  }

  if (!data || data.transaction_count === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Card
          sx={{
            background: GRADIENTS.primary,
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          }}
        >
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 60, mb: 2, opacity: 0.9 }} />
            <Typography variant="h4" fontWeight={700} gutterBottom>
              No expense data available
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mb: 4, maxWidth: 500, mx: 'auto' }}>
              Connect your Gmail to start tracking expenses.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Prepare chart data
  const categoryData = Object.entries(data.category_breakdown).map(([code, info], index) => ({
    name: info.name,
    value: info.amount,
    percentage: info.percentage,
    fill: GRADIENTS.donut[index % GRADIENTS.donut.length],
    solidColor: DONUT_COLORS_SOLID[index % DONUT_COLORS_SOLID.length],
  }));

  const monthlyData = Object.entries(data.monthly_breakdown).map(([month, info], index) => ({
    month: month.substring(0, 3),
    fullMonth: month,
    amount: info.amount,
    count: info.count,
    trend: index > 0 
      ? ((info.amount - Object.values(data.monthly_breakdown)[index - 1]?.amount || 0) / (Object.values(data.monthly_breakdown)[index - 1]?.amount || 1) * 100)
      : 0,
  }));

  // Enhanced monthly data with moving average
  const enhancedMonthlyData = monthlyData.map((item, index, arr) => {
    const movingAvg = index >= 2 
      ? (arr[index].amount + arr[index - 1].amount + arr[index - 2].amount) / 3 
      : item.amount;
    
    return {
      ...item,
      movingAverage: movingAvg,
    };
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={4}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 3,
            }}
          >
            <Box>
              <Typography
                variant="h3"
                fontWeight={800}
                sx={{
                  background: GRADIENTS.primary,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  mb: 1,
                }}
              >
                Expense Analytics
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Professional insights from your spending patterns
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel sx={{ fontWeight: 600 }}>Time Period</InputLabel>
                <Select
                  value={days}
                  label="Time Period"
                  onChange={(e) => setDays(Number(e.target.value))}
                  sx={{
                    bgcolor: 'white',
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(CHART_COLORS.primary, 0.3),
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: CHART_COLORS.primary,
                    },
                  }}
                >
                  <MenuItem value={7}>Last 7 days</MenuItem>
                  <MenuItem value={30}>Last 30 days</MenuItem>
                  <MenuItem value={90}>Last 90 days</MenuItem>
                  <MenuItem value={365}>Last year</MenuItem>
                </Select>
              </FormControl>
              <IconButton
                onClick={loadDashboard}
                sx={{
                  bgcolor: 'white',
                  color: CHART_COLORS.primary,
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.1)',
                  '&:hover': {
                    bgcolor: CHART_COLORS.primary,
                    color: 'white',
                    transform: 'translateY(-2px)',
                    transition: 'all 0.3s ease',
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Charts Section */}
          <Grid container spacing={3}>
            {/* Spending Distribution - Professional Donut Chart */}
            <Grid item xs={12} lg={6}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
                  border: '1px solid',
                  borderColor: alpha(CHART_COLORS.primary, 0.1),
                  height: '100%',
                  background: 'white',
                  overflow: 'hidden',
                  position: 'relative',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: GRADIENTS.primary,
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${alpha(CHART_COLORS.primary, 0.1)} 0%, ${alpha(CHART_COLORS.indigo, 0.05)} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PieChartIcon sx={{ color: CHART_COLORS.primary, fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: CHART_COLORS.primary }}>
                        Spending Distribution
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Category-wise expense breakdown
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ height: 340, position: 'relative' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={categoryData}
                          cx="50%"
                          cy="50%"
                          innerRadius={80}
                          outerRadius={120}
                          paddingAngle={1}
                          dataKey="value"
                          stroke="white"
                          strokeWidth={3}
                          cornerRadius={10}
                        >
                          {categoryData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={GRADIENTS.donut[index % GRADIENTS.donut.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip content={<PieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    
                    {/* Center text with professional design */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                        background: 'white',
                        borderRadius: '50%',
                        width: 140,
                        height: 140,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        border: `2px solid ${alpha(CHART_COLORS.primary, 0.1)}`,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        TOTAL
                      </Typography>
                      <Typography variant="h4" fontWeight={800} sx={{ color: CHART_COLORS.primary, lineHeight: 1.2 }}>
                        {data.currency} {data.total_spent.toFixed(0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {data.transaction_count} transactions
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Legend */}
                  <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
                    {categoryData.slice(0, 6).map((item, index) => (
                      <Chip
                        key={index}
                        label={`${item.name} (${item.percentage.toFixed(0)}%)`}
                        size="small"
                        sx={{
                          bgcolor: alpha(DONUT_COLORS_SOLID[index], 0.1),
                          color: CHART_COLORS.primary,
                          fontWeight: 600,
                          px: 1.5,
                          py: 0.5,
                          border: `1px solid ${alpha(DONUT_COLORS_SOLID[index], 0.2)}`,
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Monthly Spending Trend - Enhanced Area Chart */}
            <Grid item xs={12} lg={6}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
                  border: '1px solid',
                  borderColor: alpha(CHART_COLORS.secondary, 0.1),
                  height: '100%',
                  background: 'white',
                  overflow: 'hidden',
                  position: 'relative',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: GRADIENTS.secondary,
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${alpha(CHART_COLORS.secondary, 0.1)} 0%, ${alpha(CHART_COLORS.emerald, 0.05)} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ShowChartIcon sx={{ color: CHART_COLORS.secondary, fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color: CHART_COLORS.secondary }}>
                        Monthly Spending Trend
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Amount spent over time with trends
                      </Typography>
                    </Box>
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                      <Chip
                        label="Actual"
                        size="small"
                        sx={{
                          bgcolor: alpha(CHART_COLORS.secondary, 0.1),
                          color: CHART_COLORS.secondary,
                          fontWeight: 600,
                        }}
                      />
                      <Chip
                        label="Trend"
                        size="small"
                        sx={{
                          bgcolor: alpha(CHART_COLORS.accent, 0.1),
                          color: CHART_COLORS.accent,
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </Box>
                  
                  <Box sx={{ height: 340 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={enhancedMonthlyData}>
                        <defs>
                          <linearGradient id="colorArea" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={CHART_COLORS.secondary} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={CHART_COLORS.secondary} stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="colorLine" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor={CHART_COLORS.secondary} />
                            <stop offset="100%" stopColor={CHART_COLORS.emerald} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid 
                          strokeDasharray="3 3" 
                          stroke={alpha('#000', 0.05)}
                          vertical={false}
                        />
                        <XAxis
                          dataKey="month"
                          axisLine={false}
                          tickLine={false}
                          tick={{ 
                            fill: theme.palette.text.secondary,
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                          padding={{ left: 20, right: 20 }}
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{ 
                            fill: theme.palette.text.secondary,
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                          tickFormatter={(value) => `${data.currency} ${value}k`}
                          padding={{ top: 20, bottom: 20 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area
                          type="monotone"
                          dataKey="amount"
                          stroke="url(#colorLine)"
                          strokeWidth={3}
                          fill="url(#colorArea)"
                          name="Amount Spent"
                          dot={{
                            stroke: CHART_COLORS.secondary,
                            strokeWidth: 3,
                            r: 5,
                            fill: 'white',
                            strokeDasharray: '',
                          }}
                          activeDot={{
                            r: 8,
                            stroke: CHART_COLORS.secondary,
                            strokeWidth: 2,
                            fill: 'white',
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="movingAverage"
                          stroke={CHART_COLORS.accent}
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          dot={false}
                          name="3-Month Average"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                  
                  {/* Stats below chart */}
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        CURRENT MONTH
                      </Typography>
                      <Typography variant="h5" fontWeight={800} sx={{ color: CHART_COLORS.secondary }}>
                        {data.currency} {monthlyData[monthlyData.length - 1]?.amount.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        MONTHLY CHANGE
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUpIcon sx={{ 
                          color: monthlyData[monthlyData.length - 1]?.trend > 0 ? CHART_COLORS.warning : CHART_COLORS.secondary,
                          fontSize: 20 
                        }} />
                        <Typography variant="h5" fontWeight={800} sx={{ 
                          color: monthlyData[monthlyData.length - 1]?.trend > 0 ? CHART_COLORS.warning : CHART_COLORS.secondary 
                        }}>
                          {Math.abs(monthlyData[monthlyData.length - 1]?.trend || 0).toFixed(1)}%
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Category Breakdown Grid */}
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
              border: '1px solid',
              borderColor: alpha(CHART_COLORS.indigo, 0.1),
              background: 'white',
              overflow: 'hidden',
              position: 'relative',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${CHART_COLORS.indigo} 0%, ${CHART_COLORS.purple} 100%)`,
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: CHART_COLORS.indigo }}>
                Category Breakdown
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Detailed view of your spending across all categories
              </Typography>
              
              <Grid container spacing={2}>
                {categoryData.map((item, index) => {
                  const solidColor = DONUT_COLORS_SOLID[index % DONUT_COLORS_SOLID.length];
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: alpha(solidColor, 0.2),
                          background: `linear-gradient(135deg, ${alpha(solidColor, 0.05)} 0%, ${alpha(solidColor, 0.02)} 100%)`,
                          height: '100%',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 8px 24px ${alpha(solidColor, 0.15)}`,
                            borderColor: solidColor,
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              background: GRADIENTS.donut[index % GRADIENTS.donut.length],
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: 14,
                            }}
                          >
                            {item.percentage.toFixed(0)}%
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {item.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Rank #{index + 1}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5, color: solidColor }}>
                          {data.currency} {item.value.toFixed(2)}
                        </Typography>
                        <Box
                          sx={{
                            height: 8,
                            bgcolor: alpha('#000', 0.05),
                            borderRadius: 4,
                            overflow: 'hidden',
                            mt: 2,
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: `${item.percentage}%`,
                              background: GRADIENTS.donut[index % GRADIENTS.donut.length],
                              borderRadius: 4,
                              transition: 'width 1s ease',
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Share: {item.percentage.toFixed(1)}%
                          </Typography>
                          <Typography variant="caption" fontWeight={600} sx={{ color: solidColor }}>
                            {((item.value / data.total_spent) * 100).toFixed(1)}% of total
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
};

export default ExpenseDashboard;